<template>
  <v-btn :loading="loading" @click="getShipmentData" class="ml-2 mr-0" small fab color="#028489">
    <v-icon color="white">
      local_printshop
    </v-icon>
  </v-btn>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import * as Sentry from '@sentry/browser'
import { shipmentService } from '@/_services'
import { dropHelpers, generalHelpers } from '@/_helpers'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import Roboto from '@/assets/fonts/Roboto-Regular-normal.js'
import RobotoBold from '@/assets/fonts/Roboto-Bold-bold.js'

export default {
  name: 'ExportPlanner',
  data () {
    return {
      shipmentWithDrops: [],
      head: [],
      body: [],
      loading: false
    }
  },
  computed: {
    ...mapGetters({
      getExportShipment: 'exportData/getExportShipment',
      getPlannerVisibleShipments: 'getPlannerVisibleShipments',
      getPlannerDropsByShipmentID: 'getPlannerDropsByShipmentID'
    }),
    ...mapGetters('cognito', ['userAttributes']),
  },
  methods: {
    ...mapActions({ fetchShipmentForExport: 'exportData/fetchShipmentForExport', showSnackbar: 'showSnackbar' }),
    async getShipmentData () {
      this.shipmentWithDrops = []
      console.log(this.getPlannerVisibleShipments)
      for (let i = 0; i < this.getPlannerVisibleShipments.length; i++) {
        this.shipmentWithDrops.push(await shipmentService.getShipmentWithDropsTransport({shipmentID: this.getPlannerVisibleShipments[i]}))
      }
      this.handleExport()
    },
    addDocSettings (doc) {
      doc.addFileToVFS('Roboto-Regular.ttf', Roboto)
      doc.addFileToVFS('Roboto-Bold.ttf', RobotoBold)
      doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal')
      doc.addFont('Roboto-Bold.ttf', 'Roboto', 'bold')
      doc.setFont('Roboto')
    },
    setFooterPdf (doc, pageX, pageY, printX, printY, generateX, generateY) {
      let pageCount = doc.internal.getNumberOfPages()
      let dateObj = new Date()
      let day = dateObj.getDate()
      // months in js are indexed from 0
      let month = dateObj.getMonth() + 1
      let year = dateObj.getFullYear()
      let hour = dateObj.getHours()
      // adds 0 in front of minutes when needed
      let minute = String(dateObj.getMinutes()).padStart(2, '0')
      let newDate = day + '/' + month + '/' + year
      let newTime = hour + ':' + minute
      
      for (let i = 0; i < pageCount; i++) {
        doc.setFontSize(14)
        doc.setPage(i)
        doc.text(pageX, pageY, this.$i18n.t('print_files.pages') + ': ' + doc.internal.getCurrentPageInfo().pageNumber + '/' + pageCount)
        doc.setFontSize(9)
        doc.text(printX, printY, `${this.$i18n.t('print_files.printed')} ${newDate} ${this.$i18n.t('print_files.at')} ${newTime} ${this.$i18n.t('print_files.by')} ${this.userAttributes.email}`)
        doc.text(generateX, generateY, this.$i18n.t('print_files.generated'))
      }
    },
    orderFile (shipment) {
      let orderRef, buyerRef, companyName, address, priority, roundedWeight, roundedVolume, city, street, streetNo, country
      let labels = []
      let weight = 0
      let volume = 0

      labels = shipment.labels

      shipment.drops.forEach((drop) => {
        for (let i = 0; i < drop.transports.length; i++) {
          weight = 0
          volume = 0
          orderRef = drop.transports[i].ref
          buyerRef = drop.destination.organization.ref
          companyName = drop.destination.organization.name

          if (drop.destination.address &&
            drop.destination.address.city !== null &&
            drop.destination.address.city !== '') {
            city = drop.destination.address.city
          } else {
            city = 'N/A'
          }

          if (drop.destination.address &&
              drop.destination.address.country !== null &&
              drop.destination.address.country !== '') {
              country = drop.destination.address.country
          } else {
              country = 'N/A'
          }

          if (drop.destination.address &&
              drop.destination.address.street !== null &&
              drop.destination.address.street !== '') {
              street = drop.destination.address.street
          } else {
              street = 'N/A'
          }

          if (drop.destination.address.address &&
              drop.destination.address.streetNo !== null &&
              drop.destination.address.streetNo !== '') {
              streetNo = drop.destination.address.streetNo
          } else {
              streetNo = 'N/A'
          }
          address = `${street} ${streetNo}, ${city} ${country}`

          if (drop.transports[i].priority && drop.transports[i].priority !== null && drop.transports[i].priority !== '') {
            priority = drop.transports[i].priority
          } else {
            priority = 'N/A'
          }
        
          drop.transports[i].units.forEach((unit) => {
            weight += unit.weight
            roundedWeight = this.$options.filters.roundValueWeight(weight, 0)
            volume += ((unit.dimensions.length * unit.dimensions.width * unit.dimensions.height) * 0.000001)
            roundedVolume = this.$options.filters.roundValue(volume, 1)
          })
          this.body.push([orderRef, labels.join(''), buyerRef, companyName, address, priority, roundedWeight, roundedVolume])
        }
      })
    },
    handleExport () {
      // console.log(this.shipmentWithDrops)
      this.head = []
      this.body = []
      this.loading = true

      let Pdf = jsPDF
      let doc = new Pdf('landscape')
      // font to Roboto so we won't have problems with encoding
      this.addDocSettings(doc)

      doc.setFontSize(14)

      doc.setFontStyle('bold')
      doc.text(this.$i18n.t('print_files.order_list').toUpperCase(), 130, 10)
      doc.setFontStyle('normal')

      this.head.push([
        { content: this.$i18n.t('print_files.order_ref') },
        { content: this.$i18n.t('print_files.shipment_labels') },
        { content: this.$i18n.t('print_files.buyer_ref') },
        { content: this.$i18n.t('print_files.company_name') },
        { content: this.$i18n.t('print_files.address') },
        { content: this.$i18n.t('orders.priority') },
        { content: this.$i18n.t('print_files.weight') + ' [KG]' },
        { content: this.$i18n.t('print_files.volume') + ' [CBM]' }
      ])

      // create a different table for each shipment
      this.shipmentWithDrops.forEach((shipment) => {
        this.orderFile(shipment)

        doc.autoTable({
          head: this.head,
          body: this.body,
          styles: { fontSize: 10, textColor: [33, 33, 33], font: 'Roboto' },
          headStyles: { fillColor: [255, 255, 255], lineWidth: 0.1 },
          columnStyles: { 5: { cellWidth: 25 }, 6: { halign: 'right', cellWidth: 20 }, 7: { halign: 'right', cellWidth: 20 } },
          theme: 'grid',
          rowPageBreak: 'avoid'
        })


        this.setFooterPdf(doc, 259, 10, 14, 203, 244, 203)
        doc.addPage()
      })
       // deleted the last empty page of the pdf added from addPage above 
       let pageCount = doc.internal.getNumberOfPages()
       doc.deletePage(pageCount)
      try {
          doc.save(`${this.$i18n.t('print_files.order_list_file_name')}.pdf`)
        } catch (err) {
          Sentry.captureException(err)
          this.showSnackbar({ message: this.$t('snackbars.error'), color: 'error' })
          this.loading = false
        }
        this.loading = false
    }
  }
}
</script>

<style lang="scss" scoped>

</style>
